export interface Error {
  category?: string;
  code?: string;
  message: string;
  offendingFields?: any;
}

export interface EffectError {
  error: FeeLookupError;
}

export interface AmountDetails {
  value: number;
  currencyCode: string;
}

export interface SettlementDetails {
  reconcileReceiveAmount: AmountDetails;
  reconcileSendAmount: AmountDetails;
  walletAddress: string;
  walletMemo: string | number;
}

export interface ConfirmRefundResponse {
  expectedPayoutDate: string;
  referenceNumber: string | number;
  settlementDetails: SettlementDetails;
  transactionId: string | number;
}

export interface RefundResponse {
  dateTimeSent?: string;
  deliveryOptionId?: string;
  fundStatus?: string;
  isRefundAllowedFlag?: boolean;
  refundFeeAmount?: AmountDetails;
  refundSendAmount?: AmountDetails;
  refundTotalAmount: AmountDetails;
  sendAmount?: AmountDetails;
  transactionDate?: string;
  transactionStatus?: string;
  errors?: Error[];
}

export interface TransactionState {
  destinationCountryData?: DestinationCountryData;
  feeLookupResponse?: Transaction; // TODO: Should this be using the feeLookupResponse Interface instead of Transaction
  sendValidateRequest?: SendValidateRequest;
  sendValidateResponse: SendValidateResponse;
  sendCommitResponse?: any;
  transactionStatusResponse?: any;
  getRefundResponse?: RefundResponse;
  confirmRefundResponse?: any;
}

export interface ConfirmRefundRequest {
  userLanguage: string;
  targetAudience: string;
  refundReasonCode: string;
  sendAmount: AmountDetails;
}

export interface SendValidateRequest {
  targetAudience?: string;
  userLanguage?: string;
  destinationCountryCode?: string;
  destinationCountrySubdivisionCode?: string;
  payoutOptionCode?: string;
  payoutAmount?: AmountDetails;
  sendAmount?: AmountDetails;
  payoutCurrencyCode?: string;
  stagingInfo?: {
    stageTransaction?: boolean;
    sendLocationAgentPartnerId?: string;
    formFreeCompletion?: boolean;
  };
  sender?: SenderRegistrationInfo;
  additionalTransactionDetails?: {
    relationshipToReceiver?: string;
    purposeOfTransactionCode?: string;
    sourceOfFundsCode?: string;
    proofOfFundsCode?: string;
    intendedUseOfMGIServicesCode?: string;
    getRelationshipToReceiver?: boolean;
  };
  receiverSameAsSender?: boolean;
}

export interface SenderRegistrationInfo {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  secondLastName?: string;
  email?: string;
  notificationLanguagePreference?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    countrySubdivisionCode?: string;
    countryCode?: string;
    postalCode?: string | number;
  };
  dateOfBirth?: string;
  mobilePhone?: {
    number?: string;
    countryDialCode?: string;
  };
  birthCountryCode?: string;
  primaryIdentification?: {
    typeCode?: string;
    id?: string;
    issueCountrySubdivisionCode?: string;
    issueCountryCode?: string;
  };
  secondaryIdentification?: {
    typeCode?: string;
    id?: string;
    issueCountrySubdivisionCode?: string;
    issueCountryCode?: string;
  };
  politicalExposedPerson?: boolean;
}

export interface SendValidateResponse {
  readyForCommit?: boolean;
  transactionId?: string;
  sendAmountDetails: SendAmountDetails;
  payoutAmountDetails: PayoutAmountDetails;
  errors?: Error[];
}

export interface DestinationCountryData {
  amount?: string;
  country?: string;
  state?: string;
  currency?: string;
}

export interface KeyValue {
  key: string;
  value: any;
}
export interface FeeQuoteRequest {
  targetAudience?: string;
  userLanguage?: string;
  destinationCountryCode?: string;
  destinationCountrySubdivisionCode?: string;
  payoutOptionCode?: string;
  payoutAmount?: AmountDetails;
  sendAmount?: AmountDetails;
  stagingInfo?: {
    stageTransaction: true;
    sendLocationAgentPartnerId: string;
    formFreeCompletion: boolean;
  };
  payoutCurrencyCode?: string;
  sendCurrencyCode?: string;
  customFields?: KeyValue[];
}

export interface FeeQuoteResponse {
  transactions?: Transaction[];
  errors?: Error[];
  senderProfile?: SenderRegistrationInfo;
  additionalInfos?: AdditionalInfos[];
  PaymentDetails?: PaymentDetails[];
  cashInWalletFeeEnabled: boolean;
  cashOutWalletFeeEnabled: boolean;
}

export interface FxRateResults {
  fxRate: string | number | undefined;
  currency: string | number | undefined;
}

export interface FxRateDetails {
  fxRate: string;
  fxRateInverted: string;
  fxRateEstimated: boolean;
  receiveCurrencyCode: string;
  sendCurrencyCode: string;
  serviceOptionCode: string;
  serviceOptionName: string;
  serviceOptionRoutingCode: string;
}

export interface FeeQuoteState {
  transactionAmount: any,
  transactionCurrency: string,
  feeQuoteData: FeeQuoteData;
  errors?: any;
  fxRateDetails: {
    fxRates: FxRateDetails[];
  };
  fxRateError: any;
}

export interface FxRequest {
  destinationCountryCode?: string;
  originatingCountryCode?: string;
  receiveCurrencyCode?: string;
  sendCurrencyCode?: string;
  targetAudience: string;
  userLanguage: string;
  operation: string;
}

export interface TransactionData {
  transactionId: string;
  payoutAmountDetails: PayAmountDetails;
  sendAmountDetails: PayAmountDetails;
}

export const ID_TYPES = {
    FEES:"FEES",
    PARTNER_FEES:"PARTNER_FEES",
    TAXES:"TAXES",
    TOTAL_PAY_AMOUNT:"TOTAL_PAY_AMOUNT"
}

export interface FeeQuoteData {
  errors?: [];
  senderProfile?: SenderRegistrationInfo;
  additionalInfos?: AdditionalInfos[];
  transactions: TransactionData[];
  paymentDetails?: PaymentDetails[];
  cashInWalletFeeEnabled: boolean;
  cashOutWalletFeeEnabled: boolean;
}

export interface PayAmountDetails {
  amount?: AmountDetails;
  fees: AmountDetails;
  taxes?: AmountDetails;
  totalAmount?: AmountDetails;
  promotionDetails?: any;
  fxRate?: string;
  fxRateEstimated?: boolean;
}

export interface FeeLookupError {
  additionalInfos?: KeyValue[];
  errors: Error[];
  transactions?: { transactionId: string }[];
}

export interface SendAmountDetails {
  amount?: AmountDetails;
  fees: AmountDetails;
  taxes?: AmountDetails;
  totalAmount?: AmountDetails;
  discounts?: AmountDetails;
  fxRate?: string;
}

export interface PayoutAmountDetails {
  amount: AmountDetails;
  fees?: AmountDetails;
  taxes?: AmountDetails;
  totalAmount?: AmountDetails;
  fxRate: null;
  fxRateEstimated: null;
}

export interface PayoutOption {
  code: string;
  categoryCode: string;
  payoutPartnerId: null;
  estimatedDeliveryDate: null;
}
export interface Transaction {
  transactionId?: string;
  sendAmountDetails?: SendAmountDetails;
  payoutAmountDetails?: PayoutAmountDetails;
  payoutOption?: PayoutOption;
  cryptoNetworkSettlementDetails?: null;
  destinationCountryData?: DestinationCountryData;
  feeLookupResponse?: FeeQuoteResponse;
  additionalInfos?: AdditionalInfos[];
}

export interface AdditionalInfos {
  key?: string;
  value?: string;
}

export interface PaymentDetails {
  accountBin: string;
  accountMgiToken: string;
  cardProvider: string;
  cardsubType: string;
  expirationDate: Date;
  lastFour: string;
  paymentProcessor: string;
}
